import { Copy, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { brl, dataBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Surface } from "@/components/ds";
import { Stat, monthOf, monthLabel } from "./shared";

const SHORTCUTS = [
  ["produtos", "Produtos"],
  ["vendas", "Vendas"],
  ["comissoes", "Comissões"],
  ["entregas", "Entregas"],
  ["relatorios", "Relatórios"],
];

/** Visão geral: só números calculados a partir dos dados reais da criadora. */
export const CreatorDashboard = ({ data, onGoTo }) => {
  const products = data.products || [];
  const orders = data.orders || [];
  const published = products.filter((p) => p.published).length;
  const inProgress = orders.filter(
    (o) => !["ENTREGUE", "CANCELADO"].includes(o.status)
  ).length;
  const link = `${window.location.origin}${data.creator.catalog_path}`;

  const byMonth = {};
  orders.forEach((o) => {
    if (o.status === "CANCELADO") return;
    const key = monthOf(o.created_at);
    byMonth[key] = (byMonth[key] || 0) + Number(o.items_total || 0);
  });
  const months = Object.keys(byMonth).sort().slice(-6);
  const peak = Math.max(1, ...months.map((m) => byMonth[m]));

  return (
    <div className="space-y-6" data-testid="creator-dashboard">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <Stat label="Produtos vinculados" value={products.length}
              hint={`${published} publicados`} testid="creator-stat-products" />
        <Stat label="Vendas" value={brl(data.sales_total)}
              hint="itens seus em pedidos pagos" testid="creator-stat-sales" />
        <Stat label="Pedidos em andamento" value={inProgress}
              hint="ainda não entregues" testid="creator-stat-open-orders" />
        <Stat label="Comissões pendentes" value={brl(data.pending_total)}
              hint="liberadas para repasse" testid="creator-stat-pending" />
        <Stat label="Comissões pagas" value={brl(data.paid_total)}
              hint="repasses registrados" testid="creator-stat-paid" />
        <Stat label="Em análise" value={data.review_count}
              hint="kits aguardando revisão" testid="creator-stat-review" />
      </div>

      <Surface className="p-5" data-testid="creator-catalog-link-card">
        <p className="font-display text-base">Seu catálogo exclusivo</p>
        <p className="mt-1 break-all text-xs text-muted-foreground">{link}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            onClick={() => {
              navigator.clipboard.writeText(link);
              toast.success("Link do seu catálogo copiado.");
            }}
            className="h-10 gap-2 rounded-full px-4"
            data-testid="creator-copy-link-btn"
          >
            <Copy className="h-4 w-4" /> Copiar link
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(data.creator.catalog_path, "_blank")}
            className="h-10 rounded-full px-4"
            data-testid="creator-open-catalog-btn"
          >
            Abrir catálogo
          </Button>
        </div>
      </Surface>

      {months.length > 0 && (
        <Surface className="p-5" data-testid="creator-sales-chart">
          <p className="font-display text-base">Vendas por mês</p>
          <div className="mt-4 flex items-end gap-3">
            {months.map((m) => (
              <div key={m} className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                <span className="font-num text-[10px] text-muted-foreground">
                  {brl(byMonth[m])}
                </span>
                <div
                  className="w-full rounded-t-md bg-brand/70 transition-all"
                  style={{ height: `${Math.max(6, (byMonth[m] / peak) * 120)}px` }}
                  data-testid={`creator-sales-bar-${m}`}
                />
                <span className="text-[10px] text-muted-foreground">{monthLabel(m)}</span>
              </div>
            ))}
          </div>
        </Surface>
      )}

      <Surface className="p-5" data-testid="creator-recent-orders">
        <p className="font-display text-base">Pedidos recentes</p>
        <ul className="mt-3 space-y-2">
          {orders.length === 0 && (
            <li className="text-sm text-muted-foreground">Nenhum pedido por enquanto.</li>
          )}
          {orders.slice(0, 5).map((o) => (
            <li key={o.number}
                className="flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-secondary/30 p-3 text-xs">
              <span className="min-w-0 truncate">
                {o.number} · {dataBR(o.created_at)}
                <span className="text-muted-foreground"> · {o.status_label}</span>
              </span>
              <span className="shrink-0 font-num">{brl(o.items_total)}</span>
            </li>
          ))}
        </ul>
      </Surface>

      <div className="flex flex-wrap gap-2" data-testid="creator-shortcuts">
        {SHORTCUTS.map(([key, label]) => (
          <Button
            key={key}
            variant="soft"
            onClick={() => onGoTo(key)}
            className="h-9 gap-1.5 rounded-full px-4 text-xs"
            data-testid={`creator-shortcut-${key}`}
          >
            {label} <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        ))}
      </div>
    </div>
  );
};
